import { ReadFile } from '@paragon/client-tools';
import { ProductionComponentInstanceStatus, Station, StationRole, SynchronizedResult } from '@paragon/contract-library';

import { DesignService } from '../connections';

export function getStationsForOrganizationShallow(): Promise<Station[]> {
  return DesignService.get('public/stations');
}

export function getStation(stationGuid: string): Promise<Station> {
  return DesignService.get(`public/stations/${stationGuid}`);
}

export function addStation(
  name: string,
  stationRole: StationRole,
  followsSchedule: boolean,
): Promise<SynchronizedResult<Station>> {
  return DesignService.post('stations', { data: { name, stationRole, followsSchedule } });
}

export function patchStation(
  stationGuid: string,
  patch: Partial<Pick<Station, 'name'>>,
): Promise<SynchronizedResult<Station>> {
  return DesignService.patch(`stations/${stationGuid}`, { data: patch });
}

export function deleteStation(stationGuid: string): Promise<void> {
  return DesignService.delete(`stations/${stationGuid}`);
}

export function addComponentDesignGuidToStation(
  stationGuid: string,
  componentDesignGuid: string,
  quantity: number | null,
): Promise<SynchronizedResult<Station>> {
  const params = new URLSearchParams();
  if (quantity !== null) params.append('quantity', quantity.toString());
  return DesignService.post(`public/stations/${stationGuid}/componentDesigns/${componentDesignGuid}`, { params });
}

export function removeComponentDesignGuidFromStation(
  stationGuid: string,
  componentDesignGuid: string,
): Promise<SynchronizedResult<Station>> {
  return DesignService.delete(`public/stations/${stationGuid}/componentDesigns/${componentDesignGuid}`);
}

export function removeComponentDesignGuidsFromStation(
  stationGuid: string,
  componentDesignGuids: string[],
): Promise<SynchronizedResult<Station>> {
  return DesignService.delete(`public/stations/${stationGuid}/componentDesigns`, { data: componentDesignGuids });
}

export function clearStation(stationGuid: string): Promise<SynchronizedResult<Station>> {
  // FIXME: Ideally we wouldn't pass `{ data: null }` here, but on the server-side
  // `[FromBody(EmptyBodyBehavior = EmptyBodyBehavior.Allow)]` doesn't seem to be working.
  return DesignService.delete(`public/stations/${stationGuid}/componentDesigns`, { data: null });
}

export function postApplyCountFileToStation(stationGuid: string, file: ReadFile): Promise<SynchronizedResult<Station>> {
  return DesignService.put(`public/stations/${stationGuid}/countFile`, {
    data: {
      filename: `${file.filename}.${file.filetype}`,
      contents: file.contents,
    },
  });
}

export function updateComponentDesignQuantity(
  stationGuid: string,
  componentDesignGuid: string,
  quantity: number,
): Promise<SynchronizedResult<Station>> {
  return DesignService.patch(
    `public/stations/${stationGuid}/componentDesign/${componentDesignGuid}/updateQuantity/${quantity}`,
  );
}

export function setCurrentProductionComponentInstanceGuid(
  stationGuid: string,
  productionComponentInstanceGuid: string | null,
  newStatus?: ProductionComponentInstanceStatus,
): Promise<SynchronizedResult<Station>> {
  const params = new URLSearchParams();
  if (newStatus) params.set('newStatus', newStatus);
  return DesignService.patch(
    `public/stations/${stationGuid}/setCurrentProductionComponentInstanceGuid/${
      productionComponentInstanceGuid === null ? '' : productionComponentInstanceGuid
    }`,
    { params },
  );
}
